import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormControl, Grid, TextField } from '@mui/material';
import AddressForm from 'components/shared/address/address-form.component';
import { Address, AddressFormDetails } from 'models/contact';
import { useFormik } from 'formik';
import { ClinicMainInfoFormData } from 'models/forms';
import { emptyMainInfoFormData } from '../clinic-form-helper';
import OrganizationNumberInput from 'components/shared/organization-number/organization-number.component';
import { Country } from 'models/country';
import { countryFromCode } from 'utils/country-helper';
import { Clinic } from 'models/clinic';
import AdminLabsAutocomplete from 'components/users/technicians/add-technicians-dialog/admin-labs-autocomplete.component';
import { OrgDropdown } from 'models/user';
import NumberOnlyTextField from 'components/number-only-text-field/number-only-text-field';

type MainInfoFormProps = {
    clinic: Partial<Clinic>;
    onChange: (clinicForm: ClinicMainInfoFormData, isFormValid: boolean) => void;
    allowFullEdit?: boolean;
    isLocalClinic?: boolean;
    defaultCountryCode?: string;
};
const MainInfoForm: FC<MainInfoFormProps> = ({
    clinic,
    onChange,
    allowFullEdit,
    isLocalClinic,
    defaultCountryCode,
}: MainInfoFormProps) => {
    const { t: translate } = useTranslation(['organization', 'organization-numbers', 'users']);
    const [currentAddressDetails, setCurrentAddressDetails] = useState<AddressFormDetails>();
    const [currentCountry, setCountry] = useState<Country>();
    const [isOrganizationNumberValid, setIsOrganizationNumberValid] = useState(true);

    useEffect(() => {
        if (clinic.contact?.address) {
            setCurrentAddressDetails({
                googleId: '',
                address: clinic.contact.address || ({} as Address),
                isCompleteAddress: true,
            });
        }
        if (clinic.contact?.address?.country) {
            setCountry(countryFromCode(clinic.contact.address.country));
        } else {
            setCountry(countryFromCode(defaultCountryCode ?? 'SE'));
        }
        formik.setValues({
            name: clinic.name,
            officialName: clinic.officialName,
            organizationNumber: clinic.organizationNumber,
            address: clinic.contact?.address,
            localLabOwner: clinic.localLabOwner,
            ptjCustomerNumber: clinic.ptjCustomerNumber,
            crediflowPartyId: clinic.crediFlow?.partyID,
        });
    }, [clinic]);

    const validate = (formData: ClinicMainInfoFormData) => {
        const errors: Record<string, string> = {};
        if (!formData.name) {
            errors.name = translate('organization:VALIDATION:CLINIC_NAME');
        }
        if (isLocalClinic && !formData.localLabOwner) {
            errors.localLabOwner = translate('users:VALIDATION:LAB');
        }
        if (!formData.officialName) {
            errors.officialName = translate('organization:VALIDATION:CLINIC_NAME');
        }
        if (!isLocalClinic && !clinic.local && !formData.organizationNumber) {
            errors.organizationNumber = translate('organization:VALIDATION:ORGANIZATION_NUMBER');
        }
        if (formData.organizationNumber && !isOrganizationNumberValid) {
            errors.organizationNumber = translate(
                `organization-numbers:${currentCountry?.alpha2Code}_INVALID_ORGANIZATION_NUMBER`,
            );
        }
        if (!formData.address) {
            errors.address = 'ADDRESS_IS_REQUIRED';
        }
        onChange(formData, Object.keys(errors).length === 0);
        return errors;
    };

    const formik = useFormik<ClinicMainInfoFormData>({
        initialValues: emptyMainInfoFormData(),
        validate,
        onSubmit: async (formData) => {
            onChange(formData, formik.isValid);
        },
    });

    const handleAddressChange = (address: Address | undefined) => {
        formik.setFieldValue('address', address);
        if (address && address?.country !== currentCountry?.alpha2Code) {
            setCountry(countryFromCode(address.country || 'SE'));
            if (formik.values.address) {
                // Only clear org number when replacing an existing address (genuine country change),
                // not when setting an address for the first time (e.g. SAME_ORGANIZATION child clinic
                // flow where the org number is pre-populated from the parent but no address exists yet).
                formik.setFieldValue('organizationNumber', '');
            }
        }
    };

    const handleOrganizationNumberChange = (value: string, isOkay: boolean) => {
        setIsOrganizationNumberValid(isOkay);
        formik.setFieldValue('organizationNumber', value);
        setTimeout(() => {
            formik.validateForm();
        }, 0);
    };

    const onLabChange = (lab: OrgDropdown | null) => {
        formik.setFieldValue('localLabOwner', lab);
    };

    const onPtjCustomerNumberChange = (value: string) => {
        formik.setFieldValue('ptjCustomerNumber', value);
    };

    const onCrediflowPartyIdChange = (value: string) => {
        if (value === '') {
            formik.setFieldValue('crediflowPartyId', undefined);
        } else {
            formik.setFieldValue('crediflowPartyId', Number(value));
        }
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} sm={isLocalClinic ? 6 : 12}>
                <TextField
                    label={translate('organization:DENTHUB_CLINIC_NAME')}
                    required
                    fullWidth
                    helperText={formik.touched.name && formik.errors.name}
                    error={formik.touched.name && Boolean(formik.errors.name)}
                    {...formik.getFieldProps('name')}
                    onChange={(e) => {
                        formik.setFieldValue('name', e.target.value);
                    }}
                />
            </Grid>
            {isLocalClinic && (
                <Grid item xs={12} sm={6}>
                    <FormControl sx={{ marginTop: 0 }} margin="dense" fullWidth>
                        <AdminLabsAutocomplete
                            onFilterChange={onLabChange}
                            fullWidth
                            helperText={formik.touched.localLabOwner && formik.errors.localLabOwner}
                            error={formik.touched.localLabOwner && Boolean(formik.errors.localLabOwner)}
                            value={(formik.values.localLabOwner as OrgDropdown) || null}
                            name="localLabOwner"
                            onBlur={formik.handleBlur}
                        />
                    </FormControl>
                </Grid>
            )}
            <Grid item xs={12} sm={6}>
                <TextField
                    label={translate('organization:ORGANIZATION_OFFICIAL_NAME')}
                    required
                    fullWidth
                    InputProps={{
                        readOnly: !allowFullEdit,
                    }}
                    helperText={formik.touched.officialName && formik.errors.officialName}
                    error={formik.touched.officialName && Boolean(formik.errors.officialName)}
                    {...formik.getFieldProps('officialName')}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                {currentCountry ? (
                    <OrganizationNumberInput
                        label={translate(
                            `organization-numbers:${currentCountry?.alpha2Code || 'SE'}_ORGANIZATION_NUMBER`,
                        )}
                        required={!isLocalClinic && !clinic.local}
                        country={currentCountry}
                        fullWidth
                        onValueChange={handleOrganizationNumberChange}
                        helperText={formik.touched.organizationNumber && formik.errors.organizationNumber}
                        error={formik.touched.organizationNumber && Boolean(formik.errors.organizationNumber)}
                        onBlur={formik.getFieldProps('organizationNumber').onBlur}
                        name={formik.getFieldProps('organizationNumber').name}
                        value={formik.getFieldProps('organizationNumber').value}
                        InputProps={{
                            readOnly: !allowFullEdit,
                        }}
                    />
                ) : undefined}
            </Grid>
            {allowFullEdit && !isLocalClinic && (
                <Grid item xs={12} sm={6}>
                    <FormControl sx={{ maxWidth: 450 }} margin="dense" fullWidth>
                        <NumberOnlyTextField
                            id="ptj-customer-number"
                            size="small"
                            label={translate('organization:CUSTOMER_NUMBER')}
                            onValueChange={onPtjCustomerNumberChange}
                            onBlur={formik.getFieldProps('ptjCustomerNumber').onBlur}
                            name={formik.getFieldProps('ptjCustomerNumber').name}
                            value={formik.getFieldProps('ptjCustomerNumber').value}
                        />
                    </FormControl>
                </Grid>
            )}
            {allowFullEdit && !isLocalClinic && (
                <Grid item xs={12} sm={6}>
                    <FormControl sx={{ maxWidth: 450 }} margin="dense" fullWidth>
                        <NumberOnlyTextField
                            id="crediFlow-partyID"
                            size="small"
                            label={translate('organization:CREDIFLOW_PARTYID')}
                            onValueChange={onCrediflowPartyIdChange}
                            onBlur={formik.getFieldProps('crediflowPartyId').onBlur}
                            name={formik.getFieldProps('crediflowPartyId').name}
                            value={formik.getFieldProps('crediflowPartyId').value}
                        />
                    </FormControl>
                </Grid>
            )}
            <Grid item xs={12}>
                <AddressForm
                    onChange={handleAddressChange}
                    defaultCountryCode={currentAddressDetails?.address.country ?? defaultCountryCode}
                    address={currentAddressDetails?.address}
                />
            </Grid>
        </Grid>
    );
};

export default MainInfoForm;
